import { createApi } from '@reduxjs/toolkit/query/react';
import {
  IMessage,
  IMessagePayload,
  IMessagesQueryParams,
  IReactToMessagePayload,
  INormalizedMessagesOfThreadResponse,
  IReportMessage,
  IReportMessageV2,
  IMessagesOfThreadResponse,
  ICreateMessageThread,
  TUpdateMessage,
  ICreateMessageResponse,
  ICreateMessageThreadResponse,
  IPostAnalyticsResponse,
  IUserReactionAPI,
  IGetUserReactionsParams,
  IReportedMessagesResponse,
  IReportedMessagesQueryParams,
} from '@ui/components';
import { QUERY } from '@constants/query.constants';
import { createEntityAdapter } from '@reduxjs/toolkit';
import { authQuery } from '../config/query.config';
import { cookieService } from '@lib/cookie.service';
import { mutateUserVoteAction } from '@ui/lib';

export const messagesV2Adapter = createEntityAdapter({
  selectId: (item: IMessage) => item.id,
  sortComparer: (a, b) => (b.tempTimeStamp || 0) - (a.tempTimeStamp || 0),
});

export const messagesV2Selector = messagesV2Adapter.getSelectors();

export const messagesApiV2 = createApi({
  reducerPath: 'messages-api-v2',
  baseQuery: authQuery,
  tagTypes: [QUERY.messages, QUERY.singleMessage, 'UserReactions'],
  endpoints: (builder) => ({
    getMessagesOfThread: builder.query<INormalizedMessagesOfThreadResponse, IMessagesQueryParams>({
      query: ({ thread_id, ...rest }) => ({
        url: `/public/v1/forum/messages_of_thread/${thread_id}`,
        params: rest,
      }),
      providesTags: (result, error, { thread_id }) => [{ type: QUERY.messages, id: thread_id }],
      transformResponse: (response: IMessagesOfThreadResponse, args) => {
        const mapped = (response?.list_of_messages || []).map((m) => {
          const utcDate = new Date(m.created_at);
          const localDate = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000);

          return {
            ...m,
            thread_id: response.thread_id,
            root_thread_details: response.root_thread_details,
            thread_details: response.thread_details,
            created_at: localDate.toISOString(),
            updated_at: new Date(
              new Date(m.updated_at).getTime() - new Date(m.updated_at).getTimezoneOffset() * 60000,
            ).toISOString(),
          };
        });

        const url = new URL(args.request.url);
        const draftParam = url.searchParams.get('draft');
        const createdByCreatorParam = url.searchParams.get('created_by_creator');
        const converstion_type = url.searchParams.get('conversation_type');

        const isDraft = draftParam === 'true';
        const isCreatedByCreator = createdByCreatorParam === 'true';

        let pinnedMessages = [];

        if (
          (!isDraft && isCreatedByCreator && converstion_type == 'community') ||
          converstion_type == 'topic'
        ) {
          pinnedMessages =
            (response?.pinned_messages || []).map((m) => {
              const utcDate = new Date(m.created_at);
              const localDate = new Date(utcDate.getTime() - utcDate.getTimezoneOffset() * 60000);

              return {
                ...m,
                thread_id: response.thread_id,
                root_thread_details: response.root_thread_details,
                thread_details: response.thread_details,
                created_at: localDate.toISOString(),
                updated_at: new Date(
                  new Date(m.updated_at).getTime() -
                    new Date(m.updated_at).getTimezoneOffset() * 60000,
                ).toISOString(),
              };
            }) || [];
        }

        const normalized = messagesV2Adapter.addMany(messagesV2Adapter.getInitialState(), mapped);

        return {
          community_details: response?.community_details,
          messages: normalized,
          pinnedMessages,
          totalPages: response?.total_pages || 0,
          totalMessagesBeforeFilter: response?.total_messages_before_filter || 0,
          thread_details: response.thread_details,
          root_thread_details: response.root_thread_details,
        };
      },
      forceRefetch: ({ currentArg, previousArg }) => {
        const isSameCreator = currentArg.creator_id === previousArg?.creator_id;
        const isSameConversation = currentArg.conversation_id === previousArg?.conversation_id;
        const isSameThread = currentArg.thread_id === previousArg?.thread_id;
        const isSamePage = currentArg.page === previousArg?.page;
        const isSameCreatedByCreator =
          currentArg.created_by_creator === previousArg?.created_by_creator;
        const isSameDraft = currentArg.draft === previousArg?.draft;
        const isSameOrdering = currentArg.ordering === previousArg?.ordering;
        return (
          !isSameCreator ||
          !isSameConversation ||
          !isSameThread ||
          !isSamePage ||
          !isSameCreatedByCreator ||
          !isSameDraft ||
          !isSameOrdering
        );
      },
      serializeQueryArgs: ({ endpointName, queryArgs }) => {
        if (!queryArgs) {
          return endpointName;
        }
        return `${endpointName}-${queryArgs.thread_id}-${queryArgs.creator_id}-${queryArgs.conversation_id}-${queryArgs.created_by_creator}-${queryArgs.draft}-${queryArgs.ordering}`;
      },
      merge: (currentState, incomingState) => {
        messagesV2Adapter.addMany(
          currentState.messages,
          messagesV2Selector.selectAll(incomingState.messages),
        );
        currentState.pinnedMessages = incomingState.pinnedMessages;
        currentState.totalPages = incomingState.totalPages;
        currentState.community_details = incomingState.community_details;
        currentState.thread_details = incomingState.thread_details;
        currentState.root_thread_details = incomingState.root_thread_details;
      },
    }),
    getMessageById: builder.query<IMessage, string>({
      query: (id: string) => {
        const user_id = cookieService.getUserId() as string;
        return {
          url: `/public/v1/forum/message/${id}`,
          params: {
            user_id,
          },
        };
      },
      providesTags: (result, error, id) => [{ type: QUERY.singleMessage, id }],
    }),
    createMessage: builder.mutation<ICreateMessageResponse, IMessagePayload>({
      query: (payload) => ({
        url: `/public/v1/forum/message`,
        method: 'POST',
        body: {
          ...payload,
          files: {
            images: payload.files?.images.map((file) => file.meta),
          },
        },
      }),
      invalidatesTags: [QUERY.singleMessage],
      async onQueryStarted(payload, { dispatch, queryFulfilled, getState }) {
        const { data } = await queryFulfilled;

        if (data) {
          const newMessage: IMessage = {
            ...data.message,
            conversation_id: data.message_thread?.conversation_id,
            thread_id: data.message_thread?.id,
            parent_thread_id: data.message_thread?.id,
            reaction: {
              downvotes: 0,
              upvotes: 0, // Must not upvote if creator
              reactions_list: {},
            },
            user_votes: {
              [data.message?.user_id]: null,
            },
            creator_id: data.message?.creator_id,
            user_id: data.message?.user_id,
            tempTimeStamp: data.message?.single_message ? 0 : Date.now(),
            user_type: 2,
            community_details: payload.optimistic_details?.community_details,
          };

          const state = getState();
          const currentQueries = messagesApiV2.util.selectInvalidatedBy(state, [
            { type: QUERY.messages },
          ]);

          const patchResults = currentQueries
            .map((query) => {
              if (query.endpointName === 'getMessagesOfThread') {
                const args = query.originalArgs as IMessagesQueryParams;
                // Only update if the query parameters match the message's parameters
                if (
                  args.creator_id === newMessage.creator_id &&
                  args.conversation_id === newMessage.conversation_id &&
                  args.thread_id === newMessage.thread_id &&
                  (!data.message.post ||
                    ((args.created_by_creator === newMessage.created_by_creator ||
                      args.created_by_creator === undefined) &&
                      (args.draft === newMessage.draft || args.draft === undefined)))
                ) {
                  return dispatch(
                    messagesApiV2.util.updateQueryData('getMessagesOfThread', args, (draft) => {
                      messagesV2Adapter.addOne(draft.messages, newMessage);
                    }),
                  );
                }
              }
              return null;
            })
            .filter(Boolean);

          try {
            await queryFulfilled;
          } catch (error) {
            patchResults.forEach((patch) => patch?.undo());
          }
        }
      },
    }),
    updateMessageCount: builder.mutation({
      queryFn: () => ({ data: null }),
      onQueryStarted: async (arg, { dispatch, getState }) => {
        const { thread_id, message_id, current_count } = arg;

        const messageApiState = getState()['messages-api-v2'];
        const queriesToUpdate: Array<{ queryArgs: any; query: any }> = [];

        Object.entries(messageApiState.queries || {}).forEach(
          ([queryKey, query]: [string, any]) => {
            if (
              (query.endpointName === 'getMessagesOfThread' ||
                query.endpointName === 'getMessageById') &&
              query.data
            ) {
              const queryArgs = query.originalArgs;

              let shouldUpdate = false;

              if (query.endpointName === 'getMessagesOfThread') {
                // Check if this thread contains the target message OR if it's the target thread
                const isTargetThread = queryArgs.thread_id === thread_id;
                const hasTargetMessage =
                  query.data?.messages?.entities?.[message_id] ||
                  query.data?.pinnedMessages?.some((m: any) => m.id === message_id);

                shouldUpdate = isTargetThread || hasTargetMessage;
              } else if (query.endpointName === 'getMessageById') {
                const isSingleMessageQuery = query.data?.id === message_id;
                shouldUpdate = isSingleMessageQuery;
              }

              if (shouldUpdate) {
                queriesToUpdate.push({ queryArgs, query });
              }
            }
          },
        );

        if (queriesToUpdate.length === 0) {
          return;
        }

        queriesToUpdate.forEach(({ queryArgs, query }) => {
          if (query.endpointName === 'getMessageById') {
            dispatch(
              messagesApiV2.util.updateQueryData('getMessageById', queryArgs, (draft) => {
                if (draft) {
                  draft.total_replies = current_count;
                }
              }),
            );
          } else {
            dispatch(
              messagesApiV2.util.updateQueryData('getMessagesOfThread', queryArgs, (draft) => {
                if (draft?.messages?.entities) {
                  const targetMessage = draft.messages.entities[message_id];
                  if (targetMessage) {
                    targetMessage.total_replies = current_count;
                  }
                }

                // Update message in pinned messages
                if (draft?.pinnedMessages) {
                  const pinnedIndex = draft.pinnedMessages.findIndex(
                    (m: any) => m.id === message_id,
                  );
                  if (pinnedIndex !== -1) {
                    draft.pinnedMessages[pinnedIndex].total_replies = current_count;
                  }
                }
              }),
            );
          }
        });
      },
    }),
    updateReactionData: builder.mutation({
      queryFn: () => ({ data: null }),
      onQueryStarted: async (arg, { dispatch, getState }) => {
        const { thread_id, message_id, reaction_data } = arg;

        const messageApiState = getState()['messages-api-v2'];
        const queriesToUpdate: Array<{ queryArgs: any; query: any }> = [];

        Object.entries(messageApiState.queries || {}).forEach(
          ([queryKey, query]: [string, any]) => {
            if (
              (query.endpointName === 'getMessagesOfThread' ||
                query.endpointName === 'getMessageById') &&
              query.data
            ) {
              const queryArgs = query.originalArgs;

              let shouldUpdate = false;

              if (query.endpointName === 'getMessagesOfThread') {
                // Check if this thread contains the target message OR if it's the target thread
                const isTargetThread = queryArgs.thread_id === thread_id;
                const hasTargetMessage =
                  query.data?.messages?.entities?.[message_id] ||
                  query.data?.pinnedMessages?.some((m: any) => m.id === message_id);

                shouldUpdate = isTargetThread || hasTargetMessage;
              } else if (query.endpointName === 'getMessageById') {
                const isSingleMessageQuery = query.data?.id === message_id;
                shouldUpdate = isSingleMessageQuery;
              }

              if (shouldUpdate) {
                queriesToUpdate.push({ queryArgs, query });
              }
            }
          },
        );

        if (queriesToUpdate.length === 0) {
          return;
        }

        queriesToUpdate.forEach(({ queryArgs, query }) => {
          if (query.endpointName === 'getMessageById') {
            dispatch(
              messagesApiV2.util.updateQueryData('getMessageById', queryArgs, (draft) => {
                if (draft) {
                  draft.reaction = {
                    ...draft.reaction,
                    reactions_list: reaction_data.reactions_list,
                    upvotes: reaction_data.upvotes,
                    downvotes: reaction_data.downvotes,
                  };

                  if (reaction_data.user_reactions) {
                    draft.user_reactions = {
                      ...draft.user_reactions,
                      ...reaction_data.user_reactions,
                    };
                  }
                  if (reaction_data.user_votes) {
                    draft.user_votes = {
                      ...draft.user_votes,
                      ...reaction_data.user_votes,
                    };
                  }
                }
              }),
            );
          } else {
            dispatch(
              messagesApiV2.util.updateQueryData('getMessagesOfThread', queryArgs, (draft) => {
                if (draft?.messages?.entities) {
                  const targetMessage = draft.messages.entities[message_id];
                  if (targetMessage) {
                    targetMessage.reaction = {
                      ...targetMessage.reaction,
                      reactions_list: reaction_data.reactions_list,
                      upvotes: reaction_data.upvotes,
                      downvotes: reaction_data.downvotes,
                    };

                    if (reaction_data.user_reactions) {
                      targetMessage.user_reactions = {
                        ...targetMessage.user_reactions,
                        ...reaction_data.user_reactions,
                      };
                    }
                    if (reaction_data.user_votes) {
                      targetMessage.user_votes = {
                        ...targetMessage.user_votes,
                        ...reaction_data.user_votes,
                      };
                    }
                  }
                }

                if (draft?.pinnedMessages) {
                  const pinnedIndex = draft.pinnedMessages.findIndex(
                    (m: any) => m.id === message_id,
                  );
                  if (pinnedIndex !== -1) {
                    draft.pinnedMessages[pinnedIndex].reaction = {
                      ...draft.pinnedMessages[pinnedIndex].reaction,
                      reactions_list: reaction_data.reactions_list,
                      upvotes: reaction_data.upvotes,
                      downvotes: reaction_data.downvotes,
                    };

                    if (reaction_data.user_reactions) {
                      draft.pinnedMessages[pinnedIndex].user_reactions = {
                        ...draft.pinnedMessages[pinnedIndex].user_reactions,
                        ...reaction_data.user_reactions,
                      };
                    }
                    if (reaction_data.user_votes) {
                      draft.pinnedMessages[pinnedIndex].user_votes = {
                        ...draft.pinnedMessages[pinnedIndex].user_votes,
                        ...reaction_data.user_votes,
                      };
                    }
                  }
                }
              }),
            );
          }
        });

        // Invalidate getUserReactions cache for this message to force refetch
        Object.entries(messageApiState.queries || {}).forEach(
          ([queryKey, query]: [string, any]) => {
            if (
              query.endpointName === 'getUserReactions' &&
              query.originalArgs?.post_id === message_id
            ) {
              dispatch(
                messagesApiV2.util.invalidateTags([{ type: 'UserReactions', id: message_id }]),
              );
            }
          },
        );
      },
    }),
    deleteMessage: builder.mutation<void, IMessagesQueryParams & { id: string }>({
      query: ({ id }) => ({
        url: `/public/v1/forum/message/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [QUERY.messages],
      async onQueryStarted(params, { dispatch, queryFulfilled, getState }) {
        const state = getState();
        const currentQueries = messagesApiV2.util.selectInvalidatedBy(state, [
          { type: QUERY.messages },
        ]);

        const patchResults = currentQueries
          .map((query) => {
            if (query.endpointName === 'getMessagesOfThread') {
              return dispatch(
                messagesApiV2.util.updateQueryData(
                  'getMessagesOfThread',
                  query.originalArgs,
                  (draft) => {
                    messagesV2Adapter.removeOne(draft.messages, params.id);
                    if (draft.pinnedMessages) {
                      draft.pinnedMessages = draft.pinnedMessages.filter((m) => m.id !== params.id);
                    }
                  },
                ),
              );
            }
            return null;
          })
          .filter(Boolean);

        try {
          await queryFulfilled;
        } catch (error) {
          patchResults.forEach((patch) => patch?.undo());
        }
      },
    }),
    reactToMessage: builder.mutation<void, IReactToMessagePayload>({
      query: ({ message_id, reaction, downvote, upvote, user_id }) => ({
        url: `/public/v1/forum/react_to_message/${message_id}`,
        method: 'PUT',
        params: {
          reaction,
          user_id,
          ...(typeof downvote === 'number' && { downvote: 1 }),
          ...(typeof upvote === 'number' && { upvote: 1 }),
        },
      }),
      async onQueryStarted(
        { message_id, slug, reaction, downvote, upvote, params },
        { dispatch, queryFulfilled },
      ) {
        const cookieUserId = cookieService.getUserId();
        const userId = cookieUserId as string;

        const updateMessage = (message: IMessage) => {
          if (!message) return message;
          let user_votes = {
            ...(message.user_votes || { [userId]: null }),
          };
          const preMutatedUserVote = message.user_votes;

          let votes = {
            ...message.reaction,
          };

          if (downvote == 1) {
            votes.downvotes = message.reaction.downvotes + downvote;
            if (preMutatedUserVote?.[userId] === 'upvote') {
              votes.upvotes = message.reaction.upvotes - 1;
            }
          }

          if (downvote == -1) {
            votes.downvotes = message.reaction.downvotes + downvote;
          }

          if (upvote == 1) {
            votes.upvotes = message.reaction.upvotes + upvote;
            if (preMutatedUserVote?.[userId] === 'downvote') {
              votes.downvotes = message.reaction.downvotes - 1;
            }
          }

          if (upvote == -1) {
            votes.upvotes = message.reaction.upvotes + upvote;
          }

          // Handle emoji reactions
          if (reaction) {
            const userReactions = message.user_reactions?.[userId] || [];
            const reactionExists = userReactions.includes(reaction);

            let newUserReactions;
            let newReactionsList = { ...(message.reaction?.reactions_list || {}) };

            if (reactionExists) {
              newUserReactions = userReactions.filter((emoji) => emoji !== reaction);
              newReactionsList[reaction] = Math.max((newReactionsList[reaction] || 0) - 1, 0);
            } else {
              newUserReactions = [...userReactions, reaction];
              newReactionsList[reaction] = (newReactionsList[reaction] || 0) + 1;
            }

            return {
              ...message,
              reaction: {
                ...message.reaction,
                ...votes,
                reactions_list: newReactionsList,
              },
              user_votes: mutateUserVoteAction(user_votes, userId, downvote, upvote),
              user_reactions: {
                ...message.user_reactions,
                [userId]: newUserReactions,
              },
            };
          } else {
            return {
              ...message,
              reaction: {
                ...message.reaction,
                ...votes,
              },
              user_votes: mutateUserVoteAction(user_votes, userId, downvote, upvote),
            };
          }
        };

        let patchListResultIfReactingToOwnMessage;
        let patchListResultIfReactingToOtherMessage;

        patchListResultIfReactingToOwnMessage = dispatch(
          messagesApiV2.util.updateQueryData('getMessagesOfThread', params, (draft) => {
            const message = messagesV2Selector.selectById(draft.messages, message_id);
            if (message) {
              messagesV2Adapter.updateOne(draft.messages, {
                id: message_id,
                changes: updateMessage(message),
              });
            }

            const pinnedMessage = draft?.pinnedMessages?.find((m) => m.id === message_id);
            if (pinnedMessage) {
              Object.assign(pinnedMessage, updateMessage(pinnedMessage));
            }
          }),
        );

        if (patchListResultIfReactingToOwnMessage.patches.length === 0) {
          patchListResultIfReactingToOtherMessage = dispatch(
            messagesApiV2.util.updateQueryData(
              'getMessagesOfThread',
              {
                ...params,
                creator_id: params.fan_id,
              },
              (draft) => {
                const message = messagesV2Selector.selectById(draft.messages, message_id);
                if (message) {
                  messagesV2Adapter.updateOne(draft.messages, {
                    id: message_id,
                    changes: updateMessage(message),
                  });
                }

                const pinnedMessage = draft?.pinnedMessages?.find((m) => m.id === message_id);
                if (pinnedMessage) {
                  Object.assign(pinnedMessage, updateMessage(pinnedMessage));
                }
              },
            ),
          );
        }

        const patchSingleById = dispatch(
          messagesApiV2.util.updateQueryData('getMessageById', message_id, (draft) => {
            if (draft) {
              Object.assign(draft, updateMessage(draft));
            }
          }),
        );

        const patchSingleBySlug = dispatch(
          messagesApiV2.util.updateQueryData('getMessageById', slug, (draft) => {
            if (draft) {
              Object.assign(draft, updateMessage(draft));
            }
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchListResultIfReactingToOwnMessage.undo();
          patchListResultIfReactingToOtherMessage.undo();
          patchSingleById.undo();
          patchSingleBySlug.undo();
        }
      },
    }),
    reportMessage: builder.mutation<void, IReportMessage>({
      query: ({ message_id, report }) => ({
        url: `/public/v1/forum/report_message/${message_id}`,
        method: 'PUT',
        params: { report },
      }),
    }),
    reportMessageV2: builder.mutation<void, IReportMessageV2>({
      query: ({ message_id, user_id, report }) => ({
        url: `/public/v1/forum/report_message_v2/${message_id}`,
        method: 'PUT',
        body: { user_id, report },
      }),
    }),
    unflagMessage: builder.mutation<void, { message_id: string }>({
      query: ({ message_id }) => ({
        url: `/public/v1/forum/unflag_message/${message_id}`,
        method: 'PUT',
      }),
      invalidatesTags: [QUERY.messages],
    }),
    updateMessage: builder.mutation<IMessage, TUpdateMessage>({
      query: ({ params, files, ...rest }) => ({
        url: `/public/v1/forum/message/${rest.id}`,
        method: 'PATCH',
        body: {
          ...rest,
          files: {
            images: files?.images.map((file) => file.meta),
          },
        },
      }),
      async onQueryStarted({ params, slug, id, ...rest }, { dispatch, queryFulfilled, getState }) {
        // Only handle the specific case of draft -> published
        if (rest.draft === false) {
          const state = getState();
          const currentQueries = messagesApiV2.util.selectInvalidatedBy(state, [
            { type: QUERY.messages },
          ]);

          // Find the message in draft query first
          const draftQuery = currentQueries.find(
            (query) =>
              query.endpointName === 'getMessagesOfThread' &&
              query.originalArgs.draft === true &&
              query.originalArgs.thread_id === params.thread_id,
          );

          if (draftQuery) {
            const draftArgs = draftQuery.originalArgs as IMessagesQueryParams;
            let messageToUpdate: IMessage | undefined;
            const draftPatch = dispatch(
              messagesApiV2.util.updateQueryData('getMessagesOfThread', draftArgs, (draft) => {
                const message = messagesV2Selector.selectById(draft.messages, id);
                if (message?.draft === true) {
                  messageToUpdate = message;
                  // Remove from draft query
                  messagesV2Adapter.removeOne(draft.messages, id);
                }
              }),
            );

            // Now add to non-draft query
            if (messageToUpdate) {
              const nonDraftArgs = { ...draftArgs, draft: false };
              const nonDraftPatch = dispatch(
                messagesApiV2.util.updateQueryData('getMessagesOfThread', nonDraftArgs, (draft) => {
                  const updatedMessage = {
                    ...messageToUpdate,
                    ...rest,
                    tempTimeStamp: Date.now(),
                    files: {
                      ...messageToUpdate.files,
                      images: rest.files?.images.map((file) => file.meta),
                    },
                  };
                  messagesV2Adapter.addOne(draft.messages, updatedMessage);
                }),
              );

              try {
                await queryFulfilled;
              } catch {
                draftPatch.undo();
                nonDraftPatch.undo();
              }
              return;
            }
          }
        }

        // For all other cases, use the existing update logic
        const patchListResult = dispatch(
          messagesApiV2.util.updateQueryData('getMessagesOfThread', params, (draft) => {
            const pinnedMessage = draft?.pinnedMessages?.find((m) => m.id === id);
            if (pinnedMessage) {
              Object.assign(pinnedMessage, {
                ...pinnedMessage,
                ...rest,
                files: {
                  ...pinnedMessage.files,
                  images: rest.files?.images?.map((file) => file.meta),
                },
              });
            } else {
              const message = messagesV2Selector.selectById(draft.messages, id);
              if (message) {
                const changes = {
                  ...message,
                  ...rest,
                  files: {
                    ...message.files,
                    images: rest.files?.images.map((file) => file.meta),
                  },
                };
                messagesV2Adapter.updateOne(draft.messages, {
                  id,
                  changes,
                });
              }
            }
          }),
        );

        const patchSingleBySlug = dispatch(
          messagesApiV2.util.updateQueryData('getMessageById', slug, (draft) => {
            if (draft) {
              Object.assign(draft, {
                ...draft,
                ...rest,
                files: {
                  ...draft.files,
                  images: rest.files?.images.map((file) => file.meta),
                },
              });
            }
          }),
        );

        const patchSingleById = dispatch(
          messagesApiV2.util.updateQueryData('getMessageById', id, (draft) => {
            if (draft) {
              Object.assign(draft, {
                ...draft,
                ...rest,
                files: {
                  ...draft.files,
                  images: rest.files?.images.map((file) => file.meta),
                },
              });
            }
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchListResult.undo();
          patchSingleBySlug.undo();
          patchSingleById.undo();
        }
      },
    }),
    createMessageThread: builder.mutation<ICreateMessageThreadResponse, ICreateMessageThread>({
      query: ({ conversation_id, message_id }) => ({
        url: `/public/v1/forum/create_message_thread/${message_id}`,
        method: 'POST',
        params: { conversation_id },
      }),
      onQueryStarted: async ({ message_id, queryParams }, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;

        if (!data) {
          return;
        }

        const updateMessageThread = (params: IMessagesQueryParams) => {
          dispatch(
            messagesApiV2.util.updateQueryData('getMessagesOfThread', params, (draft) => {
              const message = messagesV2Selector.selectById(draft.messages, message_id);
              const pinnedMessageIndex = draft?.pinnedMessages?.findIndex(
                (m) => m.id === message_id,
              );

              if (pinnedMessageIndex !== -1) {
                draft.pinnedMessages = draft.pinnedMessages.map((message, index) =>
                  index === pinnedMessageIndex
                    ? { ...message, child_thread_id: data.message_thread.id }
                    : message,
                );
              }

              if (!message) return;
              messagesV2Adapter.updateOne(draft.messages, {
                id: message_id,
                changes: {
                  ...message,
                  child_thread_id: data.message_thread.id,
                },
              });
            }),
          );
        };

        // Update for both creator and fan views
        updateMessageThread(queryParams);
        updateMessageThread({ ...queryParams, creator_id: queryParams.fan_id });
      },
    }),
    getPostAnalytic: builder.query<IPostAnalyticsResponse, string>({
      query: (message_id: string) => ({
        url: `/public/v1/forum/message_statistics/${message_id}`,
      }),
      transformResponse: (response: IPostAnalyticsResponse) => {
        return {
          engagement_ratio: response?.engagement_ratio,
          total_votes: response?.total_votes,
          total_reactions: response?.total_reactions,
          total_engagement: response?.total_engagement,
          total_messages: response?.total_messages,
          total_fans: response?.total_fans,
        };
      },
      providesTags: (result, error, message_id) => [{ type: QUERY.singleMessage, id: message_id }],
    }),
    shareMessage: builder.mutation<void, string>({
      query: (message_id) => ({
        url: `/public/v1/forum/increase_shares/${message_id}`,
        method: 'PUT',
      }),
    }),
    pinMessage: builder.mutation<
      IMessage,
      {
        message_id: string;
        user_id: string;
        pin: boolean;
        thread_id: string;
        conversation_id: string;
      }
    >({
      query: ({ message_id, pin, user_id }) => ({
        url: `/public/v1/forum/toggle_pin_message/${message_id}?pin=${pin}&user_id=${user_id}`,
        method: 'PUT',
      }),
      async onQueryStarted(
        { message_id, pin, user_id, thread_id, conversation_id },
        { dispatch, queryFulfilled, getState },
      ) {
        const messageApiState = getState()['messages-api-v2'];
        const patchResults: Array<{ undo: () => void } | null> = [];

        // Find all active queries for getMessagesOfThread that contain this message
        Object.entries(messageApiState.queries || {}).forEach(
          ([queryKey, query]: [string, any]) => {
            if (query.endpointName === 'getMessagesOfThread' && query.data) {
              const args = query.originalArgs as IMessagesQueryParams;

              // Check if this query contains the target message (either in messages or pinnedMessages)
              const hasMessageInMessages = query.data?.messages?.entities?.[message_id];
              const hasMessageInPinned = query.data?.pinnedMessages?.some(
                (m: any) => m.id === message_id,
              );
              const isTargetThread =
                args.thread_id === thread_id && args.conversation_id === conversation_id;

              // Update if it's the target thread OR if the message exists in this query's data
              if (isTargetThread || hasMessageInMessages || hasMessageInPinned) {
                const patchResult = dispatch(
                  messagesApiV2.util.updateQueryData('getMessagesOfThread', args, (draft) => {
                    if (pin === true) {
                      // Find the message in the normalized messages state
                      const message = messagesV2Selector.selectById(draft.messages, message_id);

                      if (message) {
                        // Remove from messages
                        messagesV2Adapter.removeOne(draft.messages, message_id);
                        // Add to pinnedMessages
                        if (!draft.pinnedMessages) {
                          draft.pinnedMessages = [];
                        }
                        draft.pinnedMessages.unshift({
                          ...message,
                          pinned_at: new Date().toISOString(),
                        });
                      }
                    }
                    if (pin === false) {
                      // Find the message in pinnedMessages
                      const pinnedMessage = draft?.pinnedMessages?.find((m) => m.id === message_id);
                      if (pinnedMessage) {
                        messagesV2Adapter.addOne(draft.messages, {
                          ...pinnedMessage,
                          pinned_at: null,
                        });
                        draft.pinnedMessages = draft.pinnedMessages.filter(
                          (item) => item.id !== message_id,
                        );
                      }
                    }
                  }),
                );
                patchResults.push(patchResult);
              }
            }
          },
        );

        try {
          await queryFulfilled;
        } catch (error) {
          patchResults.forEach((patch) => patch?.undo());
        }
      },
    }),
    getUserReactions: builder.query<IUserReactionAPI[], IGetUserReactionsParams>({
      query: (params) => ({
        url: `/public/v1/forum/get_user_reactions/${params.post_id}`,
        params: {
          page: params.page,
          page_size: params.page_size,
        },
      }),
      providesTags: (result, error, arg) => [{ type: 'UserReactions' as const, id: arg.post_id }],
    }),
    getReportedMessages: builder.query<IReportedMessagesResponse, IReportedMessagesQueryParams>({
      query: (params) => ({
        url: `/public/v1/forum/reported_messages`,
        params: {
          creator_id: params.creator_id,
          page: params.page || 1,
          page_size: params.page_size || 6,
        },
      }),
    }),
  }),
});

export const {
  useGetMessageByIdQuery,
  useCreateMessageMutation,
  useDeleteMessageMutation,
  useGetMessagesOfThreadQuery,
  useReactToMessageMutation,
  useReportMessageMutation,
  useReportMessageV2Mutation,
  useUnflagMessageMutation,
  useUpdateMessageMutation,
  useCreateMessageThreadMutation,
  useLazyGetMessagesOfThreadQuery,
  useShareMessageMutation,
  usePinMessageMutation,
  useLazyGetPostAnalyticQuery,
  useUpdateMessageCountMutation,
  useUpdateReactionDataMutation,
  useLazyGetUserReactionsQuery,
  useGetReportedMessagesQuery,
  useLazyGetReportedMessagesQuery,
} = messagesApiV2;
